import React, { useEffect, useReducer, useRef } from 'react'
import { apiGet, apiPost } from '../generic/Api_Functions'
import PasswordDialog from './password/PasswordDialog'
import { Alert, Snackbar } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import ChecklistDialog from './checklist/ChecklistDialog'
import { isCellRunning } from './CellGeneralStatus'

/**
 * CheckDialog component. This will show the password window and the checklist after.
 * The password step is shown only when the REQUIRE_PASSWORD setting is enabled.
 * The checklist acts as a cell start-up gate: it is skipped when the cell is already running.
 * When neither step applies the action is accepted directly.
 * @param {Object} props
 * @param {Boolean} props.open If the dialog should be open
 * @param {Function} props.onClose Function called when the dialog must be closed
 * @param {Function} props.onAccept This method will be called when all steps (password, checklist) has been accepted. If null the cell will be set to AUTOMATIC. Null by default
 * @param {boolean} props.fixtureUse If true, check if fixture is compatible on devices
 * @returns
 */
const CheckDialog = ({ open, onClose, onAccept = null, fixtureUse = true }) => {
  const onDialogStatus = (state, action) => {
    if (action === 'OPEN_PASSWORD') {
      return { passwordDialogOpen: true, checkListOpen: false, snackbar: false }
    } else if (action === 'OPEN_CHECKLIST') {
      return { passwordDialogOpen: false, checkListOpen: true, snackbar: false }
    } else if (action === 'CLOSE') {
      return { ...state, passwordDialogOpen: false, checkListOpen: false }
    } else if (action === 'CHECKLIST_OK') {
      return { ...state, checkListOpen: false, snackbar: true }
    } else if (action === 'CLOSE_SNACKBAR') {
      return { ...state, snackbar: false }
    }
    return state
  }

  const [dialogStatus, dispatchDialogStatus] = useReducer(onDialogStatus, { passwordDialogOpen: false, checkListOpen: false, snackbar: false })
  // Whether the checklist step must be skipped (cell already running). Captured when the flow opens.
  const skipChecklist = useRef(false)
  const { t_ } = useSmartTranslation()

  // Runs the final action (custom onAccept, or set the cell to AUTOMATIC by default) and closes the flow.
  const executeAction = async () => {
    if (onAccept) {
      onAccept()
    } else {
      await apiPost('cellGeneralStatus', 'AUTOMATIC')
    }
    onClose()
  }

  // After the password step (or when it is skipped) either open the checklist or accept directly.
  // Kept in a ref so the open effect can call the latest version without re-running on every render.
  const proceedAfterPassword = useRef(() => {})
  proceedAfterPassword.current = () => {
    if (skipChecklist.current) {
      executeAction()
      dispatchDialogStatus('CLOSE')
    } else {
      dispatchDialogStatus('OPEN_CHECKLIST')
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }
    const initializeFlow = async () => {
      // The checklist is a start-up gate: skip it when the cell is already running.
      skipChecklist.current = isCellRunning()
      // REQUIRE_PASSWORD setting controls whether the password step is requested.
      // Default to requiring it as a safe fallback when the setting can't be read.
      let requirePassword = true
      try {
        const setting = await apiGet('settings/REQUIRE_PASSWORD')
        requirePassword = setting?.value === 'true'
      } catch (error) {
        requirePassword = true
      }
      if (requirePassword) {
        dispatchDialogStatus('OPEN_PASSWORD')
      } else {
        proceedAfterPassword.current()
      }
    }
    initializeFlow()
  }, [open])

  const dispatchClose = () => {
    dispatchDialogStatus('CLOSE')
    onClose()
  }

  const handleChecklistAccept = async () => {
    await executeAction()
    dispatchDialogStatus('CHECKLIST_OK')
  }

  return (
    <>
      <PasswordDialog open={dialogStatus.passwordDialogOpen} onClose={dispatchClose} onAccept={() => proceedAfterPassword.current()} />
      <ChecklistDialog open={dialogStatus.checkListOpen} onClose={dispatchClose} onAccept={handleChecklistAccept} fixtureUse={fixtureUse} />
      <Snackbar
        open={dialogStatus.snackbar}
        autoHideDuration={6000}
        onClose={() => { dispatchDialogStatus('CLOSE_SNACKBAR') }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => { dispatchDialogStatus('CLOSE_SNACKBAR') }} elevation={6} variant='filled' severity='success'>
          {t_('CHECKLIST IS APPROVED!')}
        </Alert>
      </Snackbar>
    </>
  )
}

export default CheckDialog
